// Inspection (nadzor): short dictated entries toggled on/off, numbered in the inspection report.
// The microphone stays open while the page is shown, so every entry starts instantly.
import { useEffect, useRef, useState } from 'react';
import type { InspectionMeta } from '../../../shared/types';
import {
  addAudioClip,
  addTextClip,
  deleteClip,
  deleteDocument,
  getLatestDocument,
  polishClip,
  retranscribeClip,
  updateClip,
  updateDocument,
  type ViewClip,
  type ViewDocument,
} from '../lib/api';
import { readManualDraft, writeManualDraft } from '../lib/drafts';
import { formatDuration, formatLongDate, formatTime, plural } from '../lib/format';
import { useDocument, useFeatures, useOutboxStatus, useRecorder } from '../lib/hooks';
import { discardRecording, type RecordingResult } from '../lib/recorder';
import { navigate } from '../lib/router';
import { copyText, documentToText, exportFileName, inspectionToDocx, saveFile } from '../export';
import { RecordingKeptCard, useContentHidden } from '../components/ContentHidden';
import { confirmDialog, isModalOpen } from '../components/Dialog';
import { EditableText, TitleInput } from '../components/EditableText';
import {
  errorText,
  exportPlaceholders,
  isAbortError,
  isTextInputTarget,
  maxSeq,
  newId,
  routeForDocument,
  useLatest,
  warnNotPersisted,
} from '../components/helpers';
import { Icon } from '../components/Icon';
import { InspectionHeaderForm } from '../components/InspectionHeader';
import { LevelMeter } from '../components/LevelMeter';
import { isMenuOpen, Menu, type MenuItem } from '../components/Menu';
import { ProjectPicker } from '../components/ProjectPicker';
import { Link, useLeaveGuard } from '../components/NavGuard';
import { PlayButton, stopPlayback, useStopPlaybackOnUnmount } from '../components/PlayButton';
import { RecordButton } from '../components/RecordButton';
import { MutedNotice, RecorderAnnouncer, RecorderErrorBox, UnsupportedNotice } from '../components/RecorderNotice';
import { ClipStatusChip, hasStatusChip } from '../components/StatusChip';
import { toast } from '../components/Toasts';
import { DocumentMissing, PageLoading } from './NotFound';

/** Shorter recordings are accidental taps / key presses and are not kept. */
const MIN_ENTRY_MS = 600;

const reportError = (fallback: string) => (err: unknown) => {
  toast(errorText(err, fallback), { kind: 'error' });
};

export function InspectionPage({ id }: { id: string }) {
  const { data, loading, error, stale, reload } = useDocument(id);
  // A running entry keeps the view (and its recorder) mounted even when the document disappears
  // meanwhile (deleted on another device): stopping it then keeps the audio for recovery.
  const [recordingActive, setRecordingActive] = useState(false);
  const lastDoc = useRef<ViewDocument | null>(null);
  useEffect(() => {
    if (data) lastDoc.current = data;
  }, [data]);
  const doc = data ?? (recordingActive ? lastDoc.current : null);

  useEffect(() => {
    if (data && data.type !== 'inspection') navigate(routeForDocument(data), { replace: true });
  }, [data]);

  if (!doc) {
    if (loading) return <PageLoading />;
    return <DocumentMissing error={error} onRetry={reload} />;
  }
  if (doc.type !== 'inspection') return <PageLoading />;
  return <InspectionView doc={doc} stale={stale} missing={!data} onRecordingChange={setRecordingActive} />;
}

interface EntrySession {
  clipId: string;
  seq: number;
}

interface InspectionViewProps {
  doc: ViewDocument;
  stale: boolean;
  /** The document no longer loads (deleted elsewhere); shown only while an entry is recorded. */
  missing: boolean;
  onRecordingChange: (recording: boolean) => void;
}

function InspectionView({ doc, stale, missing, onRecordingChange }: InspectionViewProps) {
  const docId = doc.id;
  const rec = useRecorder('dictation');
  const features = useFeatures().data;
  const hidden = useContentHidden();
  // The typed entry draft is kept per user and inspection (lib/drafts.ts), so it survives leaving
  // the page and is never shown to anyone else. Keyed by the user who opened the page (the page is
  // unmounted when someone else signs in).
  const [draftUser] = useState(() => features?.user?.id ?? null);
  const online = useOutboxStatus().online;
  useStopPlaybackOnUnmount();

  const [entry, setEntry] = useState<EntrySession | null>(null);
  const entryRef = useRef<EntrySession | null>(null);
  const startPromise = useRef<Promise<void> | null>(null);
  const finishing = useRef<Promise<boolean> | null>(null);
  const prepared = useRef(false);
  const pttActive = useRef(false);
  const lastSeq = useRef(0);
  const [saving, setSaving] = useState(false);
  const [manualDraft, setManualDraftState] = useState(() => readManualDraft(draftUser, docId));
  // A draft left from an earlier visit is shown right away.
  const [manualOpen, setManualOpen] = useState(() => manualDraft !== '');
  const manualFocus = useRef(false);
  const [busy, setBusy] = useState<'export' | 'copy' | null>(null);

  const setManualDraft = (text: string) => {
    setManualDraftState(text);
    writeManualDraft(draftUser, docId, text);
  };

  const clips = doc.clips;
  // Without entries the report still has its header (the export then says "Ni vnosov.").
  const exportable = clips.length > 0 || Object.values(doc.meta).some((v) => typeof v === 'string' && v.trim() !== '');
  const nextSeq = () => Math.max(maxSeq(clips), lastSeq.current) + 1;

  const clearEntry = () => {
    entryRef.current = null;
    setEntry(null);
  };

  // ---------- dictation ----------

  /** Must run inside the click / key handler (user gesture for the audio context on iOS). */
  const startEntry = () => {
    if (entryRef.current || finishing.current || !rec.supported) return;
    // The loudspeaker would be recorded into the new entry (and iOS may reroute it to the earpiece).
    stopPlayback();
    if (!prepared.current) {
      prepared.current = true;
      // Keep the microphone open until the page is left: the next entries start instantly.
      rec.prepare().catch(() => {
        prepared.current = false;
      });
    }
    const info: EntrySession = { clipId: newId(), seq: nextSeq() };
    // Reserved now: a typed entry added while this one is recorded gets the next number.
    lastSeq.current = Math.max(lastSeq.current, info.seq);
    entryRef.current = info;
    setEntry(info);
    const promise = rec
      .start({ docId, clipId: info.clipId, type: 'inspection', title: doc.title, seq: info.seq, diarize: false })
      .catch(() => {
        // rec.error carries the message.
        if (entryRef.current === info) clearEntry();
      });
    startPromise.current = promise;
    void promise.finally(() => {
      if (startPromise.current === promise) startPromise.current = null;
    });
  };

  /** Stops the entry and queues it. Resolves true when the audio is safe (outbox or recovery). */
  const finishEntry = (): Promise<boolean> => {
    if (finishing.current) return finishing.current;
    const info = entryRef.current;
    if (!info) return Promise.resolve(true);
    setSaving(true);
    const promise = (async () => {
      // A quick tap or key press can end before the recorder has started.
      if (startPromise.current) await startPromise.current;
      if (entryRef.current !== info) return true;
      let result: RecordingResult;
      try {
        result = await rec.stop();
      } catch (err) {
        toast(errorText(err, 'Snemanja ni bilo mogoče ustaviti.'), { kind: 'error' });
        return false;
      }
      if (result.durationMs < MIN_ENTRY_MS || result.blob.size === 0) {
        if (result.recoveryId) await discardRecording(result.recoveryId).catch(() => {});
        toast('Vnos je bil prekratek in ni shranjen.', { kind: 'warning', durationMs: 2_500 });
        return true;
      }
      try {
        const { persisted } = await addAudioClip(docId, {
          id: info.clipId,
          blob: result.blob,
          mimeType: result.mimeType,
          durationMs: result.durationMs,
          createdAt: result.startedAt,
          seq: info.seq,
          diarize: false,
        });
        lastSeq.current = Math.max(lastSeq.current, info.seq);
        if (!persisted) {
          // Only in memory: its recovery copy (if any) is kept until the server has the audio.
          warnNotPersisted(result.recoveryId !== null ? 'recoverable' : 'lost');
        } else if (result.recoveryId) {
          await discardRecording(result.recoveryId).catch((err: unknown) => console.warn('[inspection] discard failed', err));
        }
        return true;
      } catch (err) {
        const kept = result.recoveryId !== null;
        toast(`${errorText(err, 'Vnosa ni bilo mogoče shraniti.')}${kept ? ' Posnetek bo ponujen za obnovitev.' : ''}`, {
          kind: 'error',
          durationMs: 0,
        });
        return kept;
      }
    })().finally(() => {
      finishing.current = null;
      clearEntry();
      setSaving(false);
    });
    finishing.current = promise;
    return promise;
  };

  const startRef = useLatest(startEntry);
  const finishRef = useLatest(finishEntry);

  const toggle = () => {
    if (entryRef.current) void finishEntry();
    else startEntry();
  };

  useEffect(() => {
    if (!rec.interrupted || !entryRef.current) return;
    void finishRef.current().then((ok) => {
      if (ok) toast('Snemanje je bilo prekinjeno. Posneti del vnosa je shranjen.', { kind: 'warning' });
    });
  }, [rec.interrupted, finishRef]);

  useLeaveGuard(entry !== null, finishEntry, {
    message: 'Vnos se še snema. Ga želite ustaviti, shraniti in zapustiti stran?',
  });

  // Desktop push-to-talk: hold Space (outside text fields) to dictate an entry.
  useEffect(() => {
    if (!rec.supported) return;
    const ignored = (e: KeyboardEvent) =>
      e.code !== 'Space' || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || isTextInputTarget(e.target) || isModalOpen() || isMenuOpen();
    const onDown = (e: KeyboardEvent) => {
      if (ignored(e)) return;
      // No page scrolling and no activation of a focused button.
      e.preventDefault();
      if (e.repeat || pttActive.current || entryRef.current) return;
      pttActive.current = true;
      startRef.current();
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      if (!pttActive.current) {
        if (!ignored(e)) e.preventDefault();
        return;
      }
      e.preventDefault();
      pttActive.current = false;
      void finishRef.current();
    };
    const onBlur = () => {
      // Releasing Space in another window would never reach us.
      if (!pttActive.current) return;
      pttActive.current = false;
      void finishRef.current();
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [rec.supported, startRef, finishRef]);

  // ---------- entries ----------

  const addManual = async (text: string): Promise<boolean> => {
    const seq = nextSeq();
    try {
      await addTextClip(docId, { id: newId(), text, createdAt: new Date().toISOString(), seq });
      lastSeq.current = Math.max(lastSeq.current, seq);
      setManualDraft('');
      return true;
    } catch (err) {
      toast(errorText(err, 'Vnosa ni bilo mogoče dodati.'), { kind: 'error' });
      return false;
    }
  };

  const exportWord = async () => {
    setBusy('export');
    try {
      // With an edit this tap just committed (a field saved on blur).
      const latest = await getLatestDocument(doc, { network: true });
      const placeholders = exportPlaceholders(latest.clips);
      if (placeholders) {
        const ok = await confirmDialog({
          title: placeholders.pending ? 'Nekateri vnosi še niso prepisani' : 'Prepis nekaterih vnosov ni uspel',
          message: `Vnosi brez besedila bodo v zapisniku označeni z ${placeholders.labels}. Želite vseeno izvoziti?`,
          confirmLabel: 'Izvozi',
        });
        if (!ok) return;
      }
      const blob = await inspectionToDocx(latest);
      await saveFile(blob, exportFileName(latest, 'docx'), { share: true });
    } catch (err) {
      if (!isAbortError(err)) toast(errorText(err, 'Izvoz v Word ni uspel.'), { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const copy = async () => {
    setBusy('copy');
    try {
      // From this device only: the clipboard needs the tap's user activation (see getLatestDocument).
      const ok = await copyText(documentToText(await getLatestDocument(doc)));
      toast(ok ? 'Zapisnik je kopiran v odložišče.' : 'Brskalnik ni dovolil kopiranja v odložišče.', { kind: ok ? 'success' : 'warning' });
    } catch (err) {
      toast(errorText(err, 'Kopiranje ni uspelo.'), { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    const ok = await confirmDialog({
      title: 'Izbriši nadzor?',
      message: `»${doc.title}« bo izbrisan skupaj z vsemi vnosi in posnetki. Tega ni mogoče razveljaviti.`,
      confirmLabel: 'Izbriši',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteDocument(docId);
      toast('Nadzor je izbrisan.', { kind: 'info' });
      navigate({ name: 'home' }, { replace: true });
    } catch (err) {
      toast(errorText(err, 'Nadzora ni bilo mogoče izbrisati.'), { kind: 'error' });
    }
  };

  const recording = entry !== null;
  const starting = recording && rec.state === 'preparing';

  useEffect(() => {
    onRecordingChange(recording);
  }, [recording, onRecordingChange]);

  // Under the login (the session ended): only a running entry; the typed draft stays in memory.
  if (hidden) return <RecordingKeptCard recording={recording} elapsedMs={rec.elapsedMs} />;

  return (
    <div className="page page--inspection page--with-bar">
      <RecorderAnnouncer state={rec.state} />
      <div className="doc-head">
        <Link to={{ name: 'home' }} className="back-link">
          <Icon name="chevronLeft" size={20} /> Začetek
        </Link>
        <div className="doc-head__row">
          <TitleInput
            value={doc.title}
            onSave={(title) => updateDocument(docId, { title }).catch(reportError('Naslova ni bilo mogoče shraniti.'))}
            ariaLabel="Naslov nadzora"
          />
          <Menu items={[{ label: 'Izbriši nadzor', icon: 'trash', danger: true, onSelect: () => void remove() }]} label="Več možnosti" />
        </div>
        <p className="doc-head__meta">
          {formatLongDate(doc.createdAt)}
          {clips.length > 0 && ` · ${plural(clips.length, ['vnos', 'vnosa', 'vnosi', 'vnosov'])}`}
        </p>
        <div className="doc-head__project">
          <ProjectPicker doc={doc} noun="nadzora" onChange={(projectId) => updateDocument(docId, { projectId }).catch(reportError('Projekta ni bilo mogoče shraniti.'))} />
        </div>
      </div>

      {missing && (
        <div className="notice notice--warning" role="alert">
          <Icon name="alert" size={20} className="notice__icon" />
          <p className="notice__content">
            Nadzora ni več na strežniku (morda je bil izbrisan v drugi napravi). Ko vnos ustavite, bo posnetek ostal v tej napravi in bo
            ponujen za obnovitev.
          </p>
        </div>
      )}
      {stale && <p className="muted small offline-hint">Brez povezave – prikazani so podatki, shranjeni v tej napravi.</p>}

      <div className="toolbar" role="toolbar" aria-label="Dejanja">
        <button type="button" className="btn btn--primary" onClick={() => void exportWord()} disabled={busy === 'export' || !exportable}>
          <Icon name={busy === 'export' ? 'spinner' : 'file'} size={20} /> Izvozi zapisnik v Word
        </button>
        <button type="button" className="btn btn--secondary" onClick={() => void copy()} disabled={busy === 'copy' || !exportable}>
          <Icon name="copy" size={20} /> Kopiraj
        </button>
      </div>

      <InspectionHeaderForm
        meta={doc.meta}
        onSave={(meta: InspectionMeta) => updateDocument(docId, { meta }).catch(reportError('Podatkov ni bilo mogoče shraniti.'))}
        hasEntries={clips.length > 0 || recording}
      />

      <section className="entries-section" aria-labelledby="entries-title">
        <h2 id="entries-title" className="section-title">
          Vnosi
        </h2>
        {clips.length === 0 ? (
          <p className="empty">
            {rec.supported
              ? 'Še ni vnosov. Tapnite »Snemaj vnos«, povejte ugotovitev in tapnite »Ustavi«. Vsak vnos se doda kot nova točka zapisnika.'
              : 'Še ni vnosov. Dodajte jih ročno.'}
          </p>
        ) : (
          <ol className="entries">
            {clips.map((clip, i) => (
              <InspectionEntry
                key={clip.id}
                docId={docId}
                clip={clip}
                number={i + 1}
                prev={clips[i - 1]}
                next={clips[i + 1]}
                ai={features?.ai === true}
                online={online}
              />
            ))}
          </ol>
        )}

        {manualOpen ? (
          <ManualEntry
            // Not when a draft from an earlier visit is restored: that would pop up the keyboard.
            autoFocus={manualFocus.current}
            number={clips.length + (recording ? 2 : 1)}
            text={manualDraft}
            onChange={setManualDraft}
            onAdd={addManual}
            onClose={() => {
              setManualDraft('');
              setManualOpen(false);
            }}
          />
        ) : (
          <button
            type="button"
            className="btn btn--ghost add-manual"
            onClick={() => {
              manualFocus.current = true;
              setManualOpen(true);
            }}
          >
            <Icon name="pencil" size={20} /> Dodaj vnos ročno
          </button>
        )}
      </section>

      <div className="bottom-bar dictation-bar" role="region" aria-label="Snemanje vnosov">
        <div className="bottom-bar__inner">
          {!rec.supported ? (
            <UnsupportedNotice alternative="Vnose lahko dodate ročno." />
          ) : (
            <>
              {rec.error && !recording && <RecorderErrorBox message={rec.error} onRetry={startEntry} />}
              <MutedNotice muted={recording && rec.muted} />
              <div className="dictation-bar__row">
                <div className="dictation-bar__side">
                  {recording && !starting ? (
                    <>
                      <span className="timer timer--sm" role="timer">
                        {formatDuration(rec.elapsedMs)}
                      </span>
                      <LevelMeter level={rec.level} />
                    </>
                  ) : saving ? (
                    <span className="muted small">Shranjujem …</span>
                  ) : rec.state === 'ready' ? (
                    <>
                      <span className="muted small">Mikrofon je pripravljen</span>
                      <LevelMeter level={rec.level} inactive />
                    </>
                  ) : (
                    <span className="muted small">Vnos {clips.length + 1}</span>
                  )}
                </div>
                <RecordButton
                  size="md"
                  recording={recording}
                  label={recording ? 'Ustavi' : 'Snemaj vnos'}
                  ariaLabel={recording ? `Ustavi in shrani vnos ${clips.length + 1}` : `Snemaj vnos ${clips.length + 1}`}
                  onClick={toggle}
                  busy={starting || saving}
                  disabled={saving || starting}
                  ptt
                />
                <div className="dictation-bar__side dictation-bar__side--end">
                  <span className="ptt-hint muted small">ali držite preslednico</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- one entry ----------

interface EntryProps {
  docId: string;
  clip: ViewClip;
  number: number;
  prev: ViewClip | undefined;
  next: ViewClip | undefined;
  ai: boolean;
  online: boolean;
}

function InspectionEntry({ docId, clip, number, prev, next, ai, online }: EntryProps) {
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [polishing, setPolishing] = useState(false);
  const label = `vnos ${number}`;
  const hasText = clip.text.trim() !== '';
  const showText = clip.status === 'done' || hasText;

  const polish = async () => {
    if (!online) {
      toast('Za urejanje z UI je potrebna internetna povezava.', { kind: 'warning' });
      return;
    }
    setPolishing(true);
    try {
      const text = await polishClip(clip.id);
      if (text.trim() === clip.text.trim()) toast('Besedilo je že urejeno – ni predlogov za popravke.', { kind: 'info' });
      else setSuggestion(text);
    } catch (err) {
      toast(errorText(err, 'Urejanje z UI ni uspelo.'), { kind: 'error' });
    } finally {
      setPolishing(false);
    }
  };

  const move = async (other: ViewClip | undefined, direction: 'up' | 'down') => {
    if (!other) return;
    // Swap positions; equal seqs (should not happen) still end up in the requested order.
    const mine = other.seq === clip.seq ? other.seq + (direction === 'up' ? -1 : 1) : other.seq;
    try {
      await updateClip(docId, clip.id, { seq: mine });
      if (other.seq !== clip.seq) await updateClip(docId, other.id, { seq: clip.seq });
    } catch (err) {
      toast(errorText(err, 'Vrstnega reda ni bilo mogoče spremeniti.'), { kind: 'error' });
    }
  };

  const retranscribe = async () => {
    if (clip.edited || hasText) {
      const ok = await confirmDialog({
        title: `Ponovno prepiši ${label}?`,
        message: 'Besedilo bo ponovno ustvarjeno iz posnetka; ročni popravki bodo izgubljeni.',
        confirmLabel: 'Ponovno prepiši',
      });
      if (!ok) return;
    }
    retranscribeClip(docId, clip.id).catch(reportError('Ponovnega prepisa ni bilo mogoče začeti.'));
  };

  const remove = async () => {
    const ok = await confirmDialog({
      title: `Izbriši ${label}?`,
      message: hasText ? `»${clip.text.slice(0, 120)}${clip.text.length > 120 ? ' …' : ''}«` : 'Vnos in njegov posnetek bosta izbrisana.',
      confirmLabel: 'Izbriši',
      danger: true,
    });
    if (ok) deleteClip(docId, clip.id).catch(reportError('Vnosa ni bilo mogoče izbrisati.'));
  };

  const items: MenuItem[] = [];
  if (ai && hasText && !clip.localPending) {
    items.push({ label: 'Uredi z UI', icon: 'sparkles', onSelect: () => void polish(), disabled: polishing });
  }
  items.push({ label: 'Premakni gor', icon: 'arrowUp', onSelect: () => void move(prev, 'up'), disabled: !prev });
  items.push({ label: 'Premakni dol', icon: 'arrowDown', onSelect: () => void move(next, 'down'), disabled: !next });
  if (clip.hasAudio && !clip.localPending) {
    items.push({ label: 'Ponovno prepiši', icon: 'refresh', onSelect: () => void retranscribe() });
  }
  items.push({ label: 'Izbriši', icon: 'trash', danger: true, separator: true, onSelect: () => void remove() });

  return (
    <li className="entry">
      <div className="entry__head">
        <span className="entry__num">{number}.</span>
        <span className="entry__time">{formatTime(clip.createdAt)}</span>
        <span className="entry__spacer" />
        <PlayButton clip={clip} label={label} />
        <Menu items={items} label={`Možnosti za ${label}`} />
      </div>
      {/* Own row: in the head it pushed the menu onto a second line on phones. */}
      {(hasStatusChip(clip) || polishing) && (
        <div className="entry__status">
          <ClipStatusChip clip={clip} />
          {polishing && (
            <span className="chip chip--pending">
              <Icon name="spinner" size={14} /> Urejam …
            </span>
          )}
        </div>
      )}

      {showText ? (
        <EditableText
          value={clip.text}
          onSave={(text) => updateClip(docId, clip.id, { text }).catch(reportError('Popravka ni bilo mogoče shraniti.'))}
          ariaLabel={`Besedilo, ${label}`}
          placeholder="V posnetku ni bilo prepoznanega govora – vpišite besedilo."
          className="entry__text"
        />
      ) : (
        <p className="entry__placeholder">
          {clip.localPending
            ? online
              ? 'Pošiljam posnetek …'
              : 'Posnetek je shranjen v napravi in čaka na prenos.'
            : clip.status === 'error'
              ? clip.error || 'Prepis ni uspel.'
              : 'Prepisujem …'}
        </p>
      )}

      {clip.status === 'error' && !clip.localPending && clip.hasAudio && (
        <button type="button" className="btn btn--secondary btn--small" onClick={() => retranscribeClip(docId, clip.id).catch(reportError('Ponovnega prepisa ni bilo mogoče začeti.'))}>
          <Icon name="refresh" size={18} /> Poskusi znova
        </button>
      )}

      {suggestion !== null && (
        <div className="suggestion" role="region" aria-label={`Predlog za ${label}`}>
          <p className="suggestion__label">
            <Icon name="sparkles" size={18} /> Predlog urejenega besedila
          </p>
          <p className="suggestion__text">{suggestion}</p>
          <div className="suggestion__actions">
            <button
              type="button"
              className="btn btn--primary btn--small"
              onClick={() => {
                updateClip(docId, clip.id, { text: suggestion }).catch(reportError('Besedila ni bilo mogoče shraniti.'));
                setSuggestion(null);
              }}
            >
              <Icon name="check" size={18} /> Sprejmi
            </button>
            <button type="button" className="btn btn--ghost btn--small" onClick={() => setSuggestion(null)}>
              Zavrni
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ---------- typed entry ----------

interface ManualEntryProps {
  number: number;
  autoFocus: boolean;
  /** The draft lives in the page (and localStorage), so closing or leaving never loses it silently. */
  text: string;
  onChange: (text: string) => void;
  onAdd: (text: string) => Promise<boolean>;
  /** Discards the draft. */
  onClose: () => void;
}

function ManualEntry({ number, autoFocus, text, onChange, onAdd, onClose }: ManualEntryProps) {
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const focusOnMount = useRef(autoFocus);
  useEffect(() => {
    const el = ref.current;
    if (!el || !focusOnMount.current) return;
    // Focusing alone does not scroll a field that is hidden behind the sticky dictation bar.
    el.focus({ preventScroll: true });
    el.scrollIntoView?.({ block: 'center' });
  }, []);

  const submit = async () => {
    const value = text.trim();
    if (!value || saving) return;
    setSaving(true);
    const ok = await onAdd(value);
    setSaving(false);
    if (ok) ref.current?.focus();
  };

  const close = async () => {
    if (text.trim()) {
      const ok = await confirmDialog({
        title: 'Zavrzi vnos?',
        message: 'Napisano besedilo ne bo dodano v zapisnik.',
        confirmLabel: 'Zavrzi',
        cancelLabel: 'Nadaljuj z urejanjem',
        danger: true,
      });
      if (!ok) {
        ref.current?.focus();
        return;
      }
    }
    onClose();
  };

  return (
    <form
      className="card manual-entry"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label htmlFor="manual-entry" className="manual-entry__label">
        Vnos {number}
      </label>
      <textarea
        id="manual-entry"
        ref={ref}
        rows={3}
        value={text}
        lang="sl"
        maxLength={100_000}
        placeholder="Vpišite ugotovitev …"
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            void close();
          }
        }}
      />
      <div className="manual-entry__actions">
        <button type="submit" className="btn btn--primary" disabled={!text.trim() || saving}>
          <Icon name="plus" size={20} /> Dodaj
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => void close()}>
          Zapri
        </button>
      </div>
    </form>
  );
}
